import { useState, useCallback, useRef, useEffect } from 'react';
import { useResizeObserver } from './useResizeObserver';

const defaultRect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  toJSON: () => null,
};

/**
 * @example
 * ```javascript
 * const [measureElemRef, elemRect] = useClientRect()
 * console.log('Elem width: ', elemRect.width )
 * ```
 * @returns {[import('react').LegacyRef<HTMLDivElement>, DOMRect]} [RefForElemtoMeasure, DOMRect: {x, y, width, height, top, right, bottom, left}]
 */
export function useClientRect() {
  let [rect, setRect] = useState(defaultRect);
  let measureElem = useRef(null);

  let setRectIfElem = useCallback(() => {
    if (measureElem.current) setRect(measureElem.current.getBoundingClientRect());
  }, []);

  let measureRefCallback = useCallback(node => {
    if (node !== null) {
      measureElem.current = node;
      if (node) setRect(node.getBoundingClientRect());
    }
  }, []);

  let [resizedDimensions] = useResizeObserver(measureElem.current);

  useEffect(() => {
    if (resizedDimensions) setRectIfElem();
  }, [resizedDimensions, setRectIfElem]);

  return [measureRefCallback, rect];
}
